import { Injectable } from '@angular/core';
import { Observable, throwError, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { CustomerDetailsManagementService } from './customer-details-management.service';
import { ContractsService } from './contracts.service';
import { InteractionsService } from './interactions.service';
import { Customer } from '../interfaces/customer.interface';
import { InsuranceCompany, RelatedCustomer } from '../interfaces/insurance-company.interface';
import { Contract } from '../interfaces/contract.interface';
import {
  CustomerDataResult,
  CompanyDataResult,
  LoadingState,
  DataLoadResult,
  AdminInteraction
} from '../interfaces/customer-service.interface';

/**
 * Service for loading and managing data in customer details component
 */
@Injectable({
  providedIn: 'root'
})
export class CustomerDataLoadingService {

  constructor(
    private managementService: CustomerDetailsManagementService,
    private contractsService: ContractsService,
    private interactionsService: InteractionsService
  ) {}

  /**
   * Load complete customer data including contracts and interactions
   */
  loadCompleteCustomerData(customerId: number): Observable<DataLoadResult<CustomerDataResult>> {
    return this.managementService.loadCustomerData(customerId).pipe(
      map(response => {
        if (!response.success || !response.data) {
          return {
            state: { loading: false, error: 'Nem sikerült betölteni az ügyfél adatait' }
          };
        }

        // Process customer data
        const result: CustomerDataResult = {};
        
        if (response.data.relatedCustomers && response.data.relatedCustomers.length === 1) {
          const customerData = response.data.relatedCustomers[0];
          
          // Create Customer object for compatibility
          result.customer = this.createCustomerFromRelatedCustomer(customerData);
          
          // Create individual customer for display
          result.individualCustomer = {
            ...customerData,
            expanded: true,
            emails: customerData.emails || [],
            phones: customerData.phones || []
          };
        }

        return {
          data: result,
          state: { loading: false, error: null }
        };
      }),
      catchError(error => {
        console.error('Error loading customer:', error);
        const errorMessage = this.getErrorMessage(error, 'ügyfél', 'customer');
        return [{ state: { loading: false, error: errorMessage } }];
      })
    );
  }

  /**
   * Load complete company data including contracts and interactions
   */
  loadCompleteCompanyData(companyId: number): Observable<DataLoadResult<CompanyDataResult>> {
    return this.managementService.loadCompanyData(companyId).pipe(
      map(response => {
        if (!response.success || !response.data) {
          return {
            state: { loading: false, error: 'Nem sikerült betölteni a cég adatait' }
          };
        }

        // Process company data
        const result: CompanyDataResult = {
          company: {
            id: response.data.id,
            companyname: response.data.companyName || 'Ismeretlen cég',
            createdat: response.data.createdAt || '',
            registrationNumber: response.data.registrationNumber || '',
            taxNumber: response.data.taxNumber || '',
            address: response.data.address || '',
            mainActivity: response.data.mainActivity || '',
            netRevenue: response.data.netRevenue,
            employeeCount: response.data.employeeCount,
            county: response.data.county || '',
            ccStatus: response.data.ccStatus,
            kumStatus: response.data.kumStatus || '',
            existingCustomer: response.data.existingCustomer,
            emails: response.data.emails || [],
            phones: response.data.phones || [],
            relatedCustomers: (response.data.relatedCustomers || []).map((customer: any) => ({
              ...customer,
              expanded: false // All panels closed by default
            }))
          }
        };

        return {
          data: result,
          state: { loading: false, error: null }
        };
      }),
      catchError(error => {
        console.error('Error loading company:', error);
        const errorMessage = this.getErrorMessage(error, 'cég', 'company');
        return [{ state: { loading: false, error: errorMessage } }];
      })
    );
  }

  /**
   * Load contracts for an entity (company or customer)
   */
  loadContractsForEntity(entityType: 'company' | 'customer', entityId: number): Observable<DataLoadResult<Contract[]>> {
    return this.contractsService.getContractsByEntity(entityType, entityId).pipe(
      map(contracts => ({
        data: contracts,
        state: { loading: false, error: null }
      })),
      catchError(error => {
        console.error('Error loading contracts:', error);
        return [{
          state: { loading: false, error: 'Hiba a szerződések betöltése során' }
        }];
      })
    );
  }

  /**
   * Load interactions for a company
   */
  loadCompanyInteractions(companyId: number): Observable<DataLoadResult<AdminInteraction[]>> {
    return this.managementService.loadCompanyInteractions(companyId).pipe(
      map(interactions => ({
        data: interactions,
        state: { loading: false, error: null }
      })),
      catchError(error => {
        console.error('Error loading company interactions:', error);
        return [{
          state: { loading: false, error: 'Hiba a logok betöltése során' }
        }];
      })
    );
  }

  /**
   * Load interactions for a customer
   */
  loadCustomerInteractions(customerId: number): Observable<DataLoadResult<AdminInteraction[]>> {
    return this.managementService.loadCustomerInteractions(customerId).pipe(
      map(interactions => ({
        data: interactions,
        state: { loading: false, error: null }
      })),
      catchError(error => {
        console.error('Error loading customer interactions:', error);
        return [{
          state: { loading: false, error: 'Hiba a logok betöltése során' }
        }];
      })
    );
  }

  /**
   * Load complete data set (main entity + contracts + interactions)
   */
  loadCompleteDataSet(
    entityType: 'company' | 'customer', 
    entityId: number
  ): Observable<DataLoadResult<CustomerDataResult | CompanyDataResult>> {
    const mainData$ = entityType === 'company' 
      ? this.loadCompleteCompanyData(entityId)
      : this.loadCompleteCustomerData(entityId);

    const contracts$ = this.loadContractsForEntity(entityType, entityId);
    
    const interactions$ = entityType === 'company'
      ? this.loadCompanyInteractions(entityId)
      : this.loadCustomerInteractions(entityId);

    return forkJoin([mainData$, contracts$, interactions$]).pipe(
      map(([mainResult, contractsResult, interactionsResult]) => {
        // Check for main data errors first
        if (mainResult.state.error) {
          return mainResult;
        }

        // Combine all data
        const combinedData = {
          ...mainResult.data,
          contracts: contractsResult.data || [],
          interactions: interactionsResult.data || []
        };

        // Collect any non-critical errors
        const errors = [
          contractsResult.state.error,
          interactionsResult.state.error
        ].filter(error => error !== null);

        return {
          data: combinedData,
          state: { 
            loading: false, 
            error: errors.length > 0 ? `Részleges betöltési hibák: ${errors.join(', ')}` : null 
          }
        };
      }),
      catchError(error => {
        console.error('Error in complete data loading:', error);
        return [{
          state: { loading: false, error: 'Hiba az adatok betöltése során' }
        }];
      })
    );
  }

  /**
   * Refresh data while maintaining expansion state
   */
  refreshDataWithState(
    entityType: 'company' | 'customer',
    entityId: number,
    currentCompany?: InsuranceCompany,
    expandedCustomerIds?: number[]
  ): Observable<DataLoadResult<CustomerDataResult | CompanyDataResult>> {
    return this.loadCompleteDataSet(entityType, entityId).pipe(
      map(result => {
        if (result.data && 'company' in result.data && result.data.company && currentCompany && expandedCustomerIds) {
          // Restore expansion states
          if (result.data.company.relatedCustomers) {
            result.data.company.relatedCustomers.forEach(customer => {
              customer.expanded = expandedCustomerIds.includes(customer.id);
            });
          }
        }
        return result;
      })
    );
  }

  /**
   * Get expanded customer IDs for state preservation
   */
  getExpandedCustomerIds(company?: InsuranceCompany): number[] {
    if (!company?.relatedCustomers) return [];
    
    return company.relatedCustomers
      .filter(customer => customer.expanded)
      .map(customer => customer.id);
  }

  /**
   * Create Customer object from RelatedCustomer data for compatibility
   */
  private createCustomerFromRelatedCustomer(customerData: any): Customer {
    return {
      biztosito: 'N/A',
      szerzodate: customerData.createdAt || '',
      biztositott: customerData.fullName,
      kotvenyszam: 'N/A',
      modozat: 'N/A',
      termek: 'N/A',
      indulas: new Date(customerData.createdAt || Date.now()),
      rendezve: null,
      statusz: 'Élő',
      gyak: 'N/A',
      dij: 0,
      allomany: 0,
      allomanyFt: 0,
      penznem: 'HUF',
      lejarat: new Date(),
      torolve: false,
      dijhatralak: 0,
      telefonszam: customerData.phones && customerData.phones.length > 0 ? customerData.phones[0].phone : '',
      email: customerData.emails && customerData.emails.length > 0 ? customerData.emails[0].email : ''
    };
  }

  /**
   * Get appropriate error message based on error response
   */
  private getErrorMessage(error: any, entityType: string, entityKey: string): string {
    if (error.status === 401) {
      return 'Nincs jogosultsága az adatok megtekintéséhez. Kérjük, jelentkezzen be újra.';
    } else if (error.status === 404) {
      return entityType === 'ügyfél' ? 'Az ügyfél nem található' : 'A cég nem található';
    } else {
      return `Hiba történt a ${entityType} adatok betöltése során`;
    }
  }
}